import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ActivatedRoute } from '@angular/router';
import { map, startWith } from 'rxjs/operators';
import { combineLatest, Subscription } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { ConfigurationService, EmployeeFormCardComponent } from '@ep/shared';
import { Employee } from '@shared/models';
import { EmployeeFormComponent } from '@app/areas/merchants/forms/employee-form/employee-form.component';
import { EmployeeFormService } from '@app/areas/merchants/services/employee-form.service';
import { EmployeesApiService } from '@app/areas/merchants/services/employees.api.service';
import { ApplicationProfileService } from '@shared/services/application-profile.service';

@Component({
  selector: 'app-employee-detail',
  templateUrl: './employee-detail.component.html',
  styleUrls: ['./employee-detail.component.scss'],
  standalone: true,
  imports: [EmployeeFormComponent, EmployeeFormCardComponent, ConfirmDialogModule, AsyncPipe, NgIf],
  providers: [ConfirmationService],
})
export class EmployeeDetailComponent implements OnInit, OnDestroy {
  private readonly employeeFormService = inject(EmployeeFormService);
  private readonly employeesApiService = inject(EmployeesApiService);
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly configurationService = inject(ConfigurationService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly route = inject(ActivatedRoute);

  protected employee: Employee | null = null;
  protected notFound = false;
  protected isSaving = false;
  protected isImpersonating = false;

  protected mode$ = this.employeeFormService.mode$;
  protected isSaveDisabled$ = this.employeeFormService.getForm().statusChanges.pipe(
    startWith(this.employeeFormService.getForm().status),
    map((status) => status !== 'VALID')
  );

  private subs = new Subscription();

  ngOnInit(): void {
    this.subs.add(
      combineLatest([
        this.route.parent!.parent!.paramMap.pipe(map((params) => Number(params.get('merchantId')))),
        this.route.paramMap.pipe(map((params) => Number(params.get('employeeId')))),
      ]).subscribe(([merchantId, employeeId]) => {
        this.loadEmployee(merchantId, employeeId);
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadEmployee(merchantId: number, employeeId: number): void {
    this.subs.add(
      this.employeesApiService.getByIdAndMerchantId(employeeId, merchantId).subscribe((employee) => {
        if (!employee) {
          this.notFound = true;
          return;
        }

        this.notFound = false;
        this.employee = employee;
        this.employeeFormService.initializeForView(employee);
      })
    );
  }

  protected edit(): void {
    if (this.employee) {
      this.employeeFormService.initializeForEdit(this.employee);
    }
  }

  protected cancelEdit(): void {
    if (this.employee) {
      this.employeeFormService.initializeForView(this.employee);
    }
  }

  protected saveEmployee(): void {
    const form = this.employeeFormService.getForm();
    if (!form.valid || !this.employee) {
      return;
    }

    const locationId = this.employee.LocationIds?.[0];
    if (!locationId) {
      this.messageService.add({
        severity: 'error',
        summary: 'ERROR: Update Employee',
        detail: 'This employee has no assigned location to save against.',
      });
      return;
    }

    this.isSaving = true;
    const formValue = form.getRawValue();
    const previousEmail = this.employee.EmailAddress;
    const newEmail = formValue.Details.EmailAddress!;
    const emailChanged = !this.emailsMatch(previousEmail, newEmail);

    this.subs.add(
      this.employeesApiService
        .update(this.employee.EmployeeId, {
          EmployeeId: this.employee.EmployeeId,
          NameFirst: formValue.Details.NameFirst!,
          NameLast: formValue.Details.NameLast!,
          EmailAddress: formValue.Details.EmailAddress!,
          PhoneNumber: formValue.Details.PhoneNumber?.replace(/\D/g, '') ?? '',
          AccessType: formValue.Access.AccessType.value,
          Pin: formValue.Terminal.Pins[0] || undefined,
          LocationId: locationId,
        })
        .subscribe((employee) => {
          this.isSaving = false;
          if (employee) {
            this.employee = employee;
            this.employeeFormService.initializeForView(employee);

            if (emailChanged) {
              this.promptResendWelcomeEmail(employee.EmployeeId, newEmail);
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: Update Employee',
              detail: 'Something went wrong while updating the employee',
            });
          }
        })
    );
  }

  private emailsMatch(a: string | null | undefined, b: string | null | undefined): boolean {
    return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
  }

  private promptResendWelcomeEmail(employeeId: number, email: string): void {
    this.confirmationService.confirm({
      header: 'Resend Welcome Email?',
      message: `Email changed to ${email}, do you want to resend welcome email and reset the password?`,
      icon: 'pi pi-envelope',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: () => this.resendWelcomeEmail(employeeId),
    });
  }

  protected impersonate(): void {
    if (!this.employee) {
      return;
    }

    const impersonateTab = window.open('', '_blank');

    this.isImpersonating = true;
    this.subs.add(
      this.employeesApiService.impersonate(this.employee.EmployeeId).subscribe((jwt) => {
        this.isImpersonating = false;
        if (!jwt) {
          impersonateTab?.close();
          this.messageService.add({
            severity: 'error',
            summary: 'ERROR: Impersonate Employee',
            detail: 'Could not impersonate this employee. You may not have access to this merchant.',
          });
          return;
        }

        const representative = this.applicationProfileService.getApplicationProfile().Representative;
        const merchantUrl =
          representative.Coalition.MerchantPortalDomain ?? this.configurationService.merchantPortalUrl;
        if (!merchantUrl) {
          impersonateTab?.close();
          this.messageService.add({
            severity: 'error',
            summary: 'ERROR: Impersonate Employee',
            detail: 'Missing a merchant portal domain to redirect to.',
          });
          return;
        }

        const impersonateUrl = `${merchantUrl}/auth/impersonate/${jwt}`;
        if (impersonateTab) {
          impersonateTab.location.href = impersonateUrl;
        } else {
          window.location.href = impersonateUrl;
        }
      })
    );
  }

  private resendWelcomeEmail(employeeId: number): void {
    this.subs.add(
      this.employeesApiService.resendWelcomeEmail(employeeId).subscribe((success) => {
        this.messageService.add(
          success
            ? {
                severity: 'success',
                summary: 'Welcome Email Sent',
                detail: 'The welcome email has been resent to the employee.',
              }
            : {
                severity: 'error',
                summary: 'ERROR: Resend Welcome Email',
                detail: 'Could not resend the welcome email. You may not have access to this merchant.',
              }
        );
      })
    );
  }
}
